import { WeatherSoilPestChangeDevicePopoverComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-change-device-popover/weather-soil-pest-change-device-popover.component';
import {
  DeviceLineCodesEnum,
  WeatherSoilPestChangeDeviceService,
} from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-change-device-popover/weather-soil-pest-change-device.service';
import { WeatherSoilPestErrorContainerComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-error-container/weather-soil-pest-error-container.component';
import { WeatherSoilPestNodataContainerComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-nodata-container/weather-soil-pest-nodata-container.component';
import {
  CropPhenologyProxy,
  GetPhenologyCameraImagesPaginatedQuery,
  GroupedDeviceInfo,
  GroupedPlantationViewModel,
  PhenologyCameraCropImagesResponse,
  PhenologyCameraCropImagesResponseResponseResult,
  PhenologyCameraImageViewModel,
} from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Swiper } from 'swiper/types';
import { NoPhenologyDeviceContainerComponent } from './no-phenology-device-container/no-phenology-device-container.component';
import { WeatherSoilPestAddConfirmDeviceComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-add-confirm-device/weather-soil-pest-add-confirm-device.component';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { PlantationService } from '@/app/core/services/plantation.service';
import { DkImageGalleryComponent } from '@/app/shared/components/dk-image-gallery/dk-image-gallery.component';
import { DkGalleryGroup, DkGalleryItem } from '@/app/shared/components/dk-image-gallery/dk-image-gallery.types';
import { DkImageViewerComponent } from '@/app/shared/components/dk-image-viewer/dk-image-viewer.component';
import { LuxonFormatPipe } from '@/app/shared/pipes/luxon-format.pipe';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { DialogService } from 'primeng/dynamicdialog';
import { SelectCustomDatesComponent } from '@/app/features/services/analysis/select-custom-dates/select-custom-dates.component';

type PhenologyImagesMode = 'strip' | 'gallery';

@Component({
  selector: 'app-phenological-images',
  imports: [
    SharedModule,
    WeatherSoilPestChangeDevicePopoverComponent,
    WeatherSoilPestNodataContainerComponent,
    WeatherSoilPestErrorContainerComponent,
    NoPhenologyDeviceContainerComponent,
    WeatherSoilPestAddConfirmDeviceComponent,
    DkImageGalleryComponent,
  ],
  templateUrl: './phenological-images.component.html',
  styleUrl: './phenological-images.component.less',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [CropPhenologyProxy, DialogService],
  viewProviders: [LuxonFormatPipe],
})
export class PhenologicalImagesComponent implements OnChanges, AfterViewInit, OnDestroy {
  private cropPhenologyProxy = inject(CropPhenologyProxy);
  weatherSoilPestChangeDeviceService = inject(WeatherSoilPestChangeDeviceService);
  private mixpanelTracker = inject(MixpanelTrackerService);
  private plantationService = inject(PlantationService);
  private translate = inject(TranslateService);
  private luxonFormat = inject(LuxonFormatPipe);
  private dialogService = inject(DialogService);
  private destroy$ = new Subject<void>();

  // Loading states
  loading = false;
  loadMoreLoading = false;
  wasThereAnError = false;
  mode: PhenologyImagesMode = 'strip';
  galleryItems: DkGalleryItem[] = [];
  galleryGroups: DkGalleryGroup[] = [];
  galleryPeriodStart?: DateTime;
  galleryPeriodEnd?: DateTime;
  galleryLoading = false;
  galleryWasThereAnError = false;
  galleryHasMore = false;
  galleryIsLoadingMore = false;
  galleryIsCustomPeriod = false;

  // Input data
  @Input() selectedPlantation: GroupedPlantationViewModel;
  selectedPhenology: GroupedDeviceInfo;

  // Pagination
  pageNumber = 1;
  pageSize = 10;
  totalCount = 0;

  // Slider data
  phenologicalImagesResponse: PhenologyCameraCropImagesResponse;
  activeSlideIndex = 0;
  maxIndex = 0;

  // ViewChildren
  @ViewChild('swiperContainer', { static: false }) swiperContainer!: ElementRef;
  @ViewChild('slidesContainerEl') slidesContainerEl!: ElementRef;
  @ViewChild(WeatherSoilPestChangeDevicePopoverComponent, { static: false })
  weatherSoilPestChangeDevicePopoverComponent!: WeatherSoilPestChangeDevicePopoverComponent;
  @ViewChild('zoomFooter') zoomFooter?: TemplateRef<{ $implicit: DkGalleryItem; index: number }>;

  swiper: Swiper;
  deviceLineCodesEnum = DeviceLineCodesEnum;

  private isLoadingMore = false;
  private swiperInitialized = false;
  private panelDestroyed = false;
  private responseInitTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private swiperRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private galleryPageSize = 20;
  private galleryCursorRequiresStepBack = false;
  private galleryRetry?: { append: boolean; endCursor?: DateTime };
  private galleryRequestGeneration = 0;
  // Every camera-scoped Strip fetch carries the generation it started in; a camera switch or a new
  // first page moves it on, so a response or settle for the previous camera is ignored.
  private stripRequestGeneration = 0;
  // The Strip shows one field's season for one camera. The field (an input) and the camera (the
  // device service) change in either order, so the Strip fetches once per distinct key of the two.
  private stripFetchKey?: string;
  private galleryEdgeEmptyRetried = false;
  private stripEdgeEmptyRetried = false;
  private zoomViewer?: DkImageViewerComponent;
  private zoomSourceMode?: PhenologyImagesMode;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['selectedPlantation']) {
      // A new field always resets, then the device subscription's replay dedupes on the key.
      this.syncStripToSelection(true);
      if (this.selectedPlantation?.activeRotationId) {
        this.subscribeToDeviceChanges();
      }
    }
  }

  ngAfterViewInit(): void {
    this.initializeSwiper();
  }

  ngOnDestroy(): void {
    this.panelDestroyed = true;
    this.clearSwiperTimeouts();
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupSwiper();
  }

  private resetComponent(): void {
    this.stripRequestGeneration += 1;
    // Superseded requests never settle into these flags, so the reset clears them.
    this.loading = false;
    this.loadMoreLoading = false;
    this.isLoadingMore = false;
    this.resetGalleryState();
    this.clearSwiperTimeouts();
    this.swiperInitialized = false;
    this.swiper = null;
    this.phenologicalImagesResponse = null;
    this.wasThereAnError = false;
    this.pageNumber = 1;
    this.activeSlideIndex = 0;
    this.maxIndex = 0;
    this.stripEdgeEmptyRetried = false;
  }

  private resetGalleryState(): void {
    this.galleryRequestGeneration += 1;
    this.mode = 'strip';
    this.galleryItems = [];
    this.galleryGroups = [];
    this.galleryPeriodStart = undefined;
    this.galleryPeriodEnd = undefined;
    this.galleryHasMore = false;
    this.galleryLoading = false;
    this.galleryIsLoadingMore = false;
    this.galleryWasThereAnError = false;
    this.galleryIsCustomPeriod = false;
    this.galleryPageSize = 20;
    this.galleryCursorRequiresStepBack = false;
    this.galleryRetry = undefined;
    this.galleryEdgeEmptyRetried = false;
  }

  private clearSwiperTimeouts(): void {
    if (this.responseInitTimeoutId != null) {
      clearTimeout(this.responseInitTimeoutId);
      this.responseInitTimeoutId = null;
    }

    if (this.swiperRetryTimeoutId != null) {
      clearTimeout(this.swiperRetryTimeoutId);
      this.swiperRetryTimeoutId = null;
    }
  }

  private subscribeToDeviceChanges(): void {
    this.weatherSoilPestChangeDeviceService.selectedPhenologyDevice$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedPhenology) => {
        // null means the field has no PhenoCam.
        this.selectedPhenology = selectedPhenology;
        this.syncStripToSelection();
      });
  }

  private syncStripToSelection(force = false): void {
    const rotationId = this.selectedPlantation?.activeRotationId;
    const assetId = this.selectedPhenology?.assetId;
    // The service emits a new field's camera before this input changes; until the field arrives, that
    // camera is not on the current field and must not be requested with its rotation.
    const onThisField = !!assetId && (this.selectedPlantation?.devices ?? []).some((device) => device.assetId === assetId);
    const key = rotationId && onThisField ? `${rotationId}|${assetId}` : undefined;
    if (!force && key === this.stripFetchKey) {
      return;
    }
    this.stripFetchKey = key;
    this.resetComponent();
    if (key) {
      this.getPhenologicalImages();
    }
  }

  getPhenologicalImages(loadMore = false, allowFinalPartialPage = false, endCursor?: DateTime): void {
    // Prevent duplicate load more requests
    if (loadMore && this.isLoadingMore) {
      return;
    }

    // Check if we have more data to load
    if (loadMore && !allowFinalPartialPage && this.pageSize * this.pageNumber >= this.totalCount) {
      return;
    }

    this.wasThereAnError = false;

    if (!loadMore) {
      this.stripRequestGeneration += 1;
      this.loading = true;
      // A superseded Load more never settles into these flags, so a first page clears them.
      this.loadMoreLoading = false;
      this.isLoadingMore = false;
      this.pageNumber = 1;
    } else {
      this.loadMoreLoading = true;
      this.isLoadingMore = true;
    }
    const generation = this.stripRequestGeneration;
    const isStale = (): boolean => generation !== this.stripRequestGeneration;

    const body = new GetPhenologyCameraImagesPaginatedQuery();
    body.rotationId = this.selectedPlantation?.activeRotationId;
    body.assetId = this.selectedPhenology.assetId;
    body.pageNumber = this.pageNumber;
    body.pageSize = this.pageSize;
    if (endCursor) {
      body.endDate = endCursor;
    }

    this.cropPhenologyProxy
      .cropPhenology_GetPhenologyCameraImagesPaginated(undefined, endCursor, body)
      .pipe(
        finalize(() => {
          if (isStale()) {
            return;
          }
          this.loading = false;
          this.loadMoreLoading = false;
          this.isLoadingMore = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: PhenologyCameraCropImagesResponseResponseResult) => {
          if (isStale()) {
            return;
          }
          if (res.isSuccess) {
            this.handleSuccessResponse(res, loadMore);
          } else {
            this.wasThereAnError = true;
            if (this.zoomSourceMode === 'strip' && this.zoomViewer) {
              this.zoomViewer.hasMore = false;
            }
          }
        },
        error: () => {
          if (isStale()) {
            return;
          }
          this.wasThereAnError = true;
          if (this.zoomSourceMode === 'strip' && this.zoomViewer) {
            this.zoomViewer.hasMore = false;
          }
        },
      });
  }

  private handleSuccessResponse(res: PhenologyCameraCropImagesResponseResponseResult, loadMore: boolean): void {
    let currentActiveIndex = 0;
    const previousItemCount = this.phenologicalImagesResponse?.images?.items?.length ?? 0;
    if (loadMore && this.swiper) {
      currentActiveIndex = this.swiper.activeIndex;
    }

    if (!loadMore) {
      this.stripEdgeEmptyRetried = false;
      this.phenologicalImagesResponse = res.result;
      this.pageSize = res.result.images.pageSize;
      this.totalCount = res.result.images.totalCount;
    } else {
      const previousItems = this.phenologicalImagesResponse.images.items ?? [];
      const incoming = res.result.images.items ?? [];
      const existingIds = new Set(previousItems.map((item) => item.imageId));
      const fresh = incoming.filter((item) => !existingIds.has(item.imageId));
      const stripEdgeStalled =
        this.zoomSourceMode === 'strip' &&
        !!this.zoomViewer &&
        fresh.length === 0 &&
        res.result.images.hasNextPage === true;

      if (stripEdgeStalled && !this.stripEdgeEmptyRetried && previousItems.length > 0) {
        this.stripEdgeEmptyRetried = true;
        this.isLoadingMore = false;
        const oldest = previousItems.reduce(
          (candidate, item) =>
            item.captureDateTime.toMillis() < candidate.captureDateTime.toMillis() ? item : candidate,
          previousItems[0]
        );
        this.getPhenologicalImages(true, true, oldest.captureDateTime.minus({ seconds: 1 }));
        return;
      }

      if (stripEdgeStalled && this.zoomViewer) {
        this.zoomViewer.hasMore = false;
        return;
      }

      this.stripEdgeEmptyRetried = false;
      this.phenologicalImagesResponse.images.items = [...previousItems, ...incoming];
      if (
        this.zoomSourceMode === 'strip' &&
        this.phenologicalImagesResponse.images.items.length > previousItemCount
      ) {
        this.zoomViewer?.setItems(this.mapStripItems());
      }
      if (this.zoomSourceMode === 'strip' && this.zoomViewer) {
        this.zoomViewer.hasMore = this.phenologicalImagesResponse.images.items.length < this.totalCount;
      }
    }

    if (this.responseInitTimeoutId != null) {
      clearTimeout(this.responseInitTimeoutId);
    }

    const generation = this.stripRequestGeneration;
    this.responseInitTimeoutId = setTimeout(() => {
      this.responseInitTimeoutId = null;
      // A camera switch inside the delay owns the Swiper now; this step belongs to the old camera.
      if (generation !== this.stripRequestGeneration || this.panelDestroyed) {
        return;
      }
      this.initializeSwiper();
      if (loadMore && this.swiper?.params) {
        this.swiper.slideTo(currentActiveIndex, 0, false);
      }
    }, 100);
  }

  private initializeSwiper(restoredSlideIndex?: number): void {
    if (this.mode !== 'strip' || this.panelDestroyed || this.wasThereAnError) {
      return;
    }

    const swiperEl = this.swiperContainer?.nativeElement as { swiper?: Swiper } | undefined;
    const swiperInstance = swiperEl?.swiper;

    if (swiperInstance?.params) {
      if (this.swiperRetryTimeoutId != null) {
        clearTimeout(this.swiperRetryTimeoutId);
        this.swiperRetryTimeoutId = null;
      }

      if (!this.swiperInitialized || this.swiper !== swiperInstance) {
        this.swiper = swiperInstance;
        this.swiperInitialized = true;
        this.setupSwiperEvents();
      }
      this.updateSwiperConfig();
      if (restoredSlideIndex !== undefined) {
        this.swiper.slideTo(restoredSlideIndex, 0, false);
      }
      return;
    }

    if ((this.phenologicalImagesResponse?.images?.items?.length ?? 0) > 0 && this.swiperRetryTimeoutId == null) {
      this.swiperRetryTimeoutId = setTimeout(() => {
        this.swiperRetryTimeoutId = null;
        if (this.mode === 'strip' && !this.panelDestroyed && !this.wasThereAnError) {
          this.initializeSwiper(restoredSlideIndex);
        }
      }, 100);
    }
  }

  private setupSwiperEvents(): void {
    if (!this.swiper) return;

    this.swiper.on('slideChange', (e: Swiper) => {
      this.activeSlideIndex = e.activeIndex;
    });

    this.swiper.on('reachEnd', () => {
      this.loadMore();
    });
  }

  private updateSwiperConfig(): void {
    if (!this.swiper?.params || !this.phenologicalImagesResponse?.images?.items) return;

    const totalSlides = this.phenologicalImagesResponse.images.items.length;
    const slidesPerView = this.getSlidesPerView();

    this.maxIndex = Math.max(0, totalSlides - slidesPerView);
    this.swiper.update();
  }

  private getSlidesPerView(): number {
    const slidesPerView = this.swiper?.params?.slidesPerView;

    if (typeof slidesPerView === 'number') {
      return slidesPerView;
    }

    // if (this.swiper.params.slidesPerView === 'auto') {
    //   return this.swiper.slidesPerViewDynamic() || 1;
    // }

    return 1;
  }

  private loadMore(): void {
    if (this.canLoadMore()) {
      this.pageNumber += 1;
      this.getPhenologicalImages(true);
    }
  }

  private canLoadMore(): boolean {
    return !this.isLoadingMore && !this.loading && this.pageSize * this.pageNumber < this.totalCount;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateSwiperConfig();
  }

  goToNextSlide(): void {
    if (!this.swiper) {
      console.warn('Swiper not ready yet');
      return;
    }

    if (this.activeSlideIndex < this.maxIndex) {
      this.swiper.slideNext();
      this.trackPhenologyPhotoViewed();
    } else if (this.canLoadMore()) {
      this.loadMore();
      this.trackPhenologyPhotoViewed();
    }
  }

  goToPreviousSlide(): void {
    if (this.activeSlideIndex === 0) {
      return;
    }

    if (!this.swiper) {
      console.warn('Swiper not ready yet');
      return;
    }

    if (this.activeSlideIndex > 0) {
      this.swiper.slidePrev();
      this.trackPhenologyPhotoViewed();
    }
  }

  private trackPhenologyPhotoViewed(focusView = false): void {
    if (!this.selectedPlantation) {
      return;
    }

    const land = this.plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);

    this.mixpanelTracker.trackServicesPhenologyPhotoViewed({
      'focus view': focusView,
      'phenology camera id': this.selectedPhenology?.assetId ?? '',
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  onClickChangeDeviceBtn(event: Event): void {
    this.weatherSoilPestChangeDevicePopoverComponent.popover.toggle(event);
  }

  openGallery(): void {
    if (
      !this.selectedPlantation?.activeRotationId ||
      !this.selectedPhenology?.assetId ||
      !this.phenologicalImagesResponse
    ) {
      return;
    }

    this.mode = 'gallery';
    this.getGalleryImages();
  }

  closeGallery(): void {
    this.galleryRequestGeneration += 1;
    this.galleryLoading = false;
    this.galleryIsLoadingMore = false;
    this.mode = 'strip';
    queueMicrotask(() => this.initializeSwiper(this.activeSlideIndex));
  }

  onGalleryItemSelected(item: DkGalleryItem): void {
    const index = this.galleryItems.findIndex((candidate) => candidate.id === item.id);
    if (index < 0) {
      return;
    }

    this.openZoom(this.galleryItems, index);
  }

  onStripCardSelected(item: PhenologyCameraImageViewModel): void {
    const items = this.mapStripItems();
    const index = this.phenologicalImagesResponse.images.items.findIndex(
      (candidate) => candidate.imageId === item.imageId
    );
    if (index < 0) {
      return;
    }

    this.openZoom(items, index);
  }

  private mapStripItems(): DkGalleryItem[] {
    return (this.phenologicalImagesResponse?.images?.items ?? []).map((item) => ({
      id: item.imageId ?? item.imageUrl ?? item.captureDateTime.toISO(),
      url: item.imageUrl ?? '',
      capturedAt: item.captureDateTime,
    }));
  }

  openZoom(items: DkGalleryItem[], activeIndex: number): void {
    const sourceMode = this.mode;
    let attachedViewer: DkImageViewerComponent | undefined;
    const zoomClosed$ = new Subject<void>();
    this.zoomSourceMode = sourceMode;
    const ref = this.dialogService.open(DkImageViewerComponent, {
      inputValues: {
        items,
        activeIndex,
        plantationOffset: this.selectedPlantation?.offSet ?? '+00:00',
        footerTemplate: this.zoomFooter,
        hasMore:
          sourceMode === 'gallery'
            ? this.galleryHasMore
            : (this.phenologicalImagesResponse?.images?.items?.length ?? 0) < this.totalCount,
      },
      data: {
        attach: (viewer: DkImageViewerComponent) => {
          attachedViewer = viewer;
          this.zoomViewer = viewer;
          viewer.reachedEnd
            .pipe(takeUntil(zoomClosed$), takeUntil(this.destroy$))
            .subscribe(() => this.onZoomReachedEnd());
          viewer.reachedStart
            .pipe(takeUntil(zoomClosed$), takeUntil(this.destroy$))
            .subscribe(() => this.onZoomReachedStart());
        },
      },
      modal: true,
      closable: false,
      showHeader: false,
      styleClass: 'fullscreen-dialog',
      width: '100%',
      height: '100%',
    });

    this.trackPhenologyPhotoViewed(true);
    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      zoomClosed$.next();
      zoomClosed$.complete();
      if (this.zoomViewer === attachedViewer) {
        this.zoomViewer = undefined;
        this.zoomSourceMode = undefined;
      }
    });
  }

  onZoomReachedEnd(): void {
    if (this.zoomSourceMode === 'gallery') {
      this.loadMoreGallery();
      return;
    }

    const loadedCount = this.phenologicalImagesResponse?.images?.items?.length ?? 0;
    if (!this.isLoadingMore && !this.loading && loadedCount < this.totalCount) {
      this.pageNumber = Math.ceil(loadedCount / this.pageSize) + 1;
      this.getPhenologicalImages(true, true);
    }
  }

  onZoomReachedStart(): void {
    // Both views load newest-first, so there is no newer page before index zero.
  }

  getGalleryImages(): void {
    this.resetGalleryPeriod();
    this.fetchGalleryPage(false);
  }

  loadMoreGallery(): void {
    if (this.galleryIsLoadingMore || !this.galleryHasMore || this.galleryItems.length === 0) {
      return;
    }

    const oldest = this.galleryItems.reduce(
      (candidate, item) => (item.capturedAt.toMillis() < candidate.toMillis() ? item.capturedAt : candidate),
      this.galleryItems[0].capturedAt
    );
    const cursor = this.galleryCursorRequiresStepBack ? oldest.minus({ seconds: 1 }) : oldest;
    this.fetchGalleryPage(true, cursor);
  }

  openGalleryPeriodDialog(): void {
    const today = DateTime.now().setZone(this.getPlantationZone());
    const ref = this.dialogService.open(SelectCustomDatesComponent, {
      modal: true,
      closable: true,
      header: this.translate.instant('doktar_app.common.change_period'),
      data: {
        startDate: this.galleryPeriodStart ? this.toDatePickerDate(this.galleryPeriodStart) : undefined,
        endDate: this.galleryPeriodEnd ? this.toDatePickerDate(this.galleryPeriodEnd) : undefined,
        maxDate: this.toDatePickerDate(today),
      },
    });

    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      const [startValue, selectedEndValue] = result?.rangeDates ?? [];
      const endValue = selectedEndValue ?? startValue;
      if (
        !(startValue instanceof Date) ||
        Number.isNaN(startValue.getTime()) ||
        !(endValue instanceof Date) ||
        Number.isNaN(endValue.getTime())
      ) {
        return;
      }

      const periodStart = this.toPlantationDate(startValue, false);
      const periodEnd = this.toPlantationDate(endValue, true);
      this.galleryPeriodStart = periodStart;
      this.galleryPeriodEnd = periodEnd;
      this.galleryIsCustomPeriod = true;
      this.fetchGalleryPage(false);
    });
  }

  clearGalleryPeriod(): void {
    this.resetGalleryPeriod();
    this.fetchGalleryPage(false);
  }

  retryGallery(): void {
    const retry = this.galleryRetry ?? { append: false };
    this.fetchGalleryPage(retry.append, retry.endCursor);
  }

  private clearGalleryRows(): void {
    this.galleryItems = [];
    this.galleryGroups = [];
    this.galleryHasMore = false;
    this.galleryCursorRequiresStepBack = false;
    this.galleryEdgeEmptyRetried = false;
  }

  private fetchGalleryPage(append: boolean, endCursor?: DateTime): void {
    const generation = ++this.galleryRequestGeneration;
    if (!append) {
      this.clearGalleryRows();
    }
    const body = new GetPhenologyCameraImagesPaginatedQuery();
    body.rotationId = this.selectedPlantation.activeRotationId;
    body.assetId = this.selectedPhenology.assetId;
    body.startDate = this.galleryPeriodStart;
    body.endDate = endCursor ?? this.galleryPeriodEnd;
    body.pageNumber = 1;
    body.pageSize = append ? this.galleryPageSize : 20;

    this.galleryWasThereAnError = false;
    if (append) {
      this.galleryIsLoadingMore = true;
    } else {
      this.galleryIsLoadingMore = false;
      this.galleryLoading = true;
    }

    this.cropPhenologyProxy
      .cropPhenology_GetPhenologyCameraImagesPaginated(body.startDate, body.endDate, body)
      .pipe(
        finalize(() => {
          if (generation !== this.galleryRequestGeneration) {
            return;
          }
          if (append) {
            this.galleryIsLoadingMore = false;
          } else {
            this.galleryLoading = false;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: PhenologyCameraCropImagesResponseResponseResult) => {
          if (generation !== this.galleryRequestGeneration) {
            return;
          }
          if (!res.isSuccess) {
            this.galleryWasThereAnError = true;
            this.galleryRetry = { append, endCursor };
            if (!append) {
              this.clearGalleryRows();
            }
            if (this.zoomSourceMode === 'gallery' && this.zoomViewer) {
              this.zoomViewer.hasMore = false;
            }
            return;
          }

          this.galleryRetry = undefined;
          this.applyGalleryResponse(res.result, append);
        },
        error: () => {
          if (generation !== this.galleryRequestGeneration) {
            return;
          }
          this.galleryWasThereAnError = true;
          this.galleryRetry = { append, endCursor };
          if (!append) {
            this.clearGalleryRows();
          }
          if (this.zoomSourceMode === 'gallery' && this.zoomViewer) {
            this.zoomViewer.hasMore = false;
          }
        },
      });
  }

  private applyGalleryResponse(response: PhenologyCameraCropImagesResponse, append: boolean): void {
    const mappedItems = (response.images.items ?? []).map((item) => ({
      id: item.imageId ?? item.imageUrl ?? item.captureDateTime.toISO(),
      url: item.imageUrl ?? '',
      capturedAt: item.captureDateTime,
    }));
    const previousItemCount = this.galleryItems.length;

    if (append) {
      const loadedIds = new Set(this.galleryItems.map((item) => item.id));
      if (mappedItems.some((item) => loadedIds.has(item.id))) {
        this.galleryCursorRequiresStepBack = true;
      }
      this.galleryItems = [...this.galleryItems, ...mappedItems.filter((item) => !loadedIds.has(item.id))];
    } else {
      this.galleryItems = mappedItems;
      this.galleryCursorRequiresStepBack = false;
    }

    this.galleryPageSize = response.images.pageSize || this.galleryPageSize;
    const added = this.galleryItems.length - previousItemCount;
    const galleryEdgeStalled =
      append &&
      added === 0 &&
      response.images.hasNextPage === true &&
      this.zoomSourceMode === 'gallery' &&
      !!this.zoomViewer;

    if (galleryEdgeStalled && !this.galleryEdgeEmptyRetried) {
      this.galleryEdgeEmptyRetried = true;
      const oldest = this.galleryItems.reduce(
        (candidate, item) => (item.capturedAt.toMillis() < candidate.toMillis() ? item.capturedAt : candidate),
        this.galleryItems[0].capturedAt
      );
      this.fetchGalleryPage(true, oldest.minus({ seconds: 1 }));
      return;
    }

    if (galleryEdgeStalled) {
      this.galleryHasMore = false;
    } else {
      this.galleryHasMore = response.images.hasNextPage;
      if (!append || added > 0) {
        this.galleryEdgeEmptyRetried = false;
      }
    }

    if (append && this.zoomSourceMode === 'gallery' && this.zoomViewer) {
      if (added > 0) {
        this.zoomViewer.setItems(this.galleryItems);
      }
      this.zoomViewer.hasMore = this.galleryHasMore;
    }
    this.galleryGroups = this.groupGalleryItems(this.galleryItems, response);
  }

  getRotationStart(response: PhenologyCameraCropImagesResponse): DateTime | undefined {
    if (!response?.currentWeek || !response.currentWeekStart) {
      return undefined;
    }

    return response.currentWeekStart
      .setZone(this.getPlantationZone())
      .startOf('day')
      .minus({ days: (response.currentWeek - 1) * 7 });
  }

  groupGalleryItems(items: DkGalleryItem[], response: PhenologyCameraCropImagesResponse): DkGalleryGroup[] {
    const rotationStart = this.getRotationStart(response);
    if (!rotationStart) {
      return this.groupGalleryItemsByIsoWeek(items);
    }

    const zone = this.getPlantationZone();
    const localRotationStart = rotationStart.setZone(zone);
    const groupedItems = new Map<number, DkGalleryItem[]>();

    for (const item of items) {
      const capturedAt = item.capturedAt.setZone(zone);
      const week = Math.floor(capturedAt.startOf('day').diff(localRotationStart.startOf('day'), 'days').days / 7) + 1;
      groupedItems.set(week, [...(groupedItems.get(week) ?? []), item]);
    }

    return [...groupedItems.entries()]
      .sort(([leftWeek], [rightWeek]) => rightWeek - leftWeek)
      .map(([week, weekItems]) => {
        const start = localRotationStart.plus({ days: (week - 1) * 7 });
        const end = start.plus({ days: 6 });
        const weekText = this.translate.instant('doktar_app.common.week_x', { arg1: week });
        return {
          key: `rotation-week-${week}`,
          label: `${weekText} · ${this.formatWeekRange(start, end, zone)}`,
          items: weekItems,
        };
      });
  }

  zoomWeekLabel(item: DkGalleryItem): string {
    const zone = this.getPlantationZone();
    const capturedAt = item.capturedAt.setZone(zone);
    const rotationStart = this.getRotationStart(this.phenologicalImagesResponse);

    if (!rotationStart) {
      const start = capturedAt.startOf('week');
      return this.formatWeekRange(start, start.plus({ days: 6 }), zone);
    }

    const localRotationStart = rotationStart.setZone(zone);
    const week = Math.floor(capturedAt.startOf('day').diff(localRotationStart.startOf('day'), 'days').days / 7) + 1;
    return this.translate.instant('doktar_app.common.week_x', { arg1: week });
  }

  zoomCapturedAt(item: DkGalleryItem): string {
    return this.luxonFormat.transform(item.capturedAt, 'd LLL y HH:mm', this.getPlantationZone());
  }

  private groupGalleryItemsByIsoWeek(items: DkGalleryItem[]): DkGalleryGroup[] {
    const zone = this.getPlantationZone();
    const groupedItems = new Map<string, { sortValue: number; start: DateTime; items: DkGalleryItem[] }>();

    for (const item of items) {
      const capturedAt = item.capturedAt.setZone(zone);
      const key = `iso-week-${capturedAt.weekYear}-${capturedAt.weekNumber}`;
      const existingGroup = groupedItems.get(key);
      groupedItems.set(key, {
        sortValue: capturedAt.weekYear * 100 + capturedAt.weekNumber,
        start: capturedAt.startOf('week'),
        items: [...(existingGroup?.items ?? []), item],
      });
    }

    return [...groupedItems.entries()]
      .sort(([, left], [, right]) => right.sortValue - left.sortValue)
      .map(([key, group]) => ({
        key,
        label: this.formatWeekRange(group.start, group.start.plus({ days: 6 }), zone),
        items: group.items,
      }));
  }

  private formatWeekRange(start: DateTime, end: DateTime, zone: string): string {
    const sameYear = start.year === end.year;
    const sameMonth = sameYear && start.month === end.month;
    const startFormat = sameMonth ? 'd' : sameYear ? 'd LLL' : 'd LLL y';

    return this.translate.instant('doktar_app.common.date_x_to_y', {
      arg1: this.luxonFormat.transform(start, startFormat, zone),
      arg2: this.luxonFormat.transform(end, 'd LLL y', zone),
    });
  }

  private getPlantationZone(): string {
    return `UTC${this.selectedPlantation?.offSet ?? '+00:00'}`;
  }

  private resetGalleryPeriod(): void {
    const rotationStart = this.getRotationStart(this.phenologicalImagesResponse);
    this.galleryPeriodStart = rotationStart;
    this.galleryPeriodEnd = rotationStart ? DateTime.now().setZone(this.getPlantationZone()) : undefined;
    this.galleryIsCustomPeriod = false;
    this.galleryPageSize = 20;
    this.galleryCursorRequiresStepBack = false;
  }

  private toPlantationDate(value: Date, endOfDay: boolean): DateTime {
    const date = DateTime.fromObject(
      {
        year: value.getFullYear(),
        month: value.getMonth() + 1,
        day: value.getDate(),
      },
      { zone: this.getPlantationZone() }
    );

    return endOfDay ? date.endOf('day') : date.startOf('day');
  }

  private toDatePickerDate(value: DateTime): Date {
    const localDate = value.setZone(this.getPlantationZone());
    return new Date(localDate.year, localDate.month - 1, localDate.day);
  }

  private cleanupSwiper(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.swiperInitialized = false;
    }
  }

  get hasMoreImages(): boolean {
    return this.pageSize * this.pageNumber < this.totalCount;
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }
}
